import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  CoworkSession,
  CoworkSessionSummary,
  CoworkMessage,
  CoworkConfig,
  CoworkPermissionRequest,
  CoworkSessionStatus,
} from '../../types/cowork';

interface CoworkState {
  sessions: CoworkSessionSummary[];
  currentSessionId: string | null;
  currentSession: CoworkSession | null;
  draftPrompt: string; // Legacy: current session draft (for backward compat)
  draftBySession: Record<string, string>; // Draft message per session
  queuedMessageBySession: Record<string, { prompt: string; skillPrompt?: string } | null>; // Queued message per session
  queueWithdrawnBySession: Record<string, boolean>; // Track if queue was withdrawn for each session
  unreadSessionIds: string[];
  isCoworkActive: boolean;
  isStreaming: boolean;
  pendingPermissions: CoworkPermissionRequest[];
  config: CoworkConfig;
}

const initialState: CoworkState = {
  sessions: [],
  currentSessionId: null,
  currentSession: null,
  draftPrompt: '', // Legacy
  draftBySession: {},
  queuedMessageBySession: {},
  queueWithdrawnBySession: {},
  unreadSessionIds: [],
  isCoworkActive: false,
  isStreaming: false,
  pendingPermissions: [],
  config: {
    workingDirectory: '',
    systemPrompt: '',
    executionMode: 'local',
    memoryEnabled: true,
    memoryImplicitUpdateEnabled: true,
    memoryLlmJudgeEnabled: false,
    memoryGuardLevel: 'strict',
    memoryUserMemoriesMaxItems: 12,
  },
};

const markSessionRead = (state: CoworkState, sessionId: string | null) => {
  if (!sessionId) return;
  state.unreadSessionIds = state.unreadSessionIds.filter((id) => id !== sessionId);
};

const markSessionUnread = (state: CoworkState, sessionId: string) => {
  if (state.currentSessionId === sessionId) return;
  if (state.unreadSessionIds.includes(sessionId)) return;
  state.unreadSessionIds.push(sessionId);
};

const coworkSlice = createSlice({
  name: 'cowork',
  initialState,
  reducers: {
    setCoworkActive(state, action: PayloadAction<boolean>) {
      state.isCoworkActive = action.payload;
    },

    setSessions(state, action: PayloadAction<CoworkSessionSummary[]>) {
      // Treat any 'running' sessions as 'idle' on load - they can't be actively
      // streaming without a live WebSocket connection after a page load/refresh
      state.sessions = action.payload.map(s => ({
        ...s,
        status: s.status === 'running' ? 'idle' as CoworkSessionStatus : s.status,
      }));
      const validSessionIds = new Set(action.payload.map((session) => session.id));
      state.unreadSessionIds = state.unreadSessionIds.filter((id) => {
        return validSessionIds.has(id) && id !== state.currentSessionId;
      });
    },

    setCurrentSessionId(state, action: PayloadAction<string | null>) {
      state.currentSessionId = action.payload;
      markSessionRead(state, action.payload);
    },

    setCurrentSession(state, action: PayloadAction<CoworkSession | null>) {
      state.currentSession = action.payload;
      if (action.payload) {
        state.currentSessionId = action.payload.id;
        if (!action.payload.id.startsWith('temp-')) {
          const { id, title, status, pinned, createdAt, updatedAt } = action.payload;
          const summary: CoworkSessionSummary = {
            id,
            title,
            status,
            pinned: pinned ?? false,
            createdAt,
            updatedAt,
          };
          const sessionIndex = state.sessions.findIndex((session) => session.id === id);
          if (sessionIndex !== -1) {
            state.sessions[sessionIndex] = {
              ...state.sessions[sessionIndex],
              ...summary,
            };
          } else {
            state.sessions.unshift(summary);
          }
        }
        markSessionRead(state, action.payload.id);
        // Sync isStreaming to the session being viewed: a 'running' session
        // shows the streaming bar even if the WS 'running' event arrived while
        // a different session was open (updateSessionStatus only sets isStreaming
        // for the currently-viewed session).
        if (!action.payload.id.startsWith('temp-')) {
          state.isStreaming = action.payload.status === 'running';
        }
      }
    },

    setDraftForSession(state, action: PayloadAction<{ sessionId: string; draft: string }>) {
      state.draftBySession[action.payload.sessionId] = action.payload.draft;
    },

    // Backward compatible action - for views without session
    setDraftPrompt(state, action: PayloadAction<string>) {
      if (state.currentSessionId) {
        state.draftBySession[state.currentSessionId] = action.payload;
      }
    },

    setQueuedMessageForSession(state, action: PayloadAction<{ sessionId: string; message: { prompt: string; skillPrompt?: string } | null }>) {
      state.queuedMessageBySession[action.payload.sessionId] = action.payload.message;
      // Also clear the withdrawal flag when setting a new queued message
      if (action.payload.message) {
        state.queueWithdrawnBySession[action.payload.sessionId] = false;
      }
    },

    setQueueWithdrawnForSession(state, action: PayloadAction<{ sessionId: string; withdrawn: boolean }>) {
      state.queueWithdrawnBySession[action.payload.sessionId] = action.payload.withdrawn;
    },

    addSession(state, action: PayloadAction<CoworkSession>) {
      const summary: CoworkSessionSummary = {
        id: action.payload.id,
        title: action.payload.title,
        status: action.payload.status,
        pinned: action.payload.pinned ?? false,
        createdAt: action.payload.createdAt,
        updatedAt: action.payload.updatedAt,
      };
      const existingIndex = state.sessions.findIndex((s) => s.id === summary.id);
      if (existingIndex !== -1) {
        state.sessions[existingIndex] = { ...state.sessions[existingIndex], ...summary };
      } else {
        state.sessions.unshift(summary);
      }
      state.currentSession = action.payload;
      state.currentSessionId = action.payload.id;
      markSessionRead(state, action.payload.id);
    },

    updateSessionStatus(state, action: PayloadAction<{ sessionId: string; status: CoworkSessionStatus }>) {
      const { sessionId, status } = action.payload;

      // Update in sessions list
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].status = status;
        state.sessions[sessionIndex].updatedAt = Date.now();
      }

      // Update current session if applicable
      if (state.currentSession?.id === sessionId) {
        state.currentSession.status = status;
        state.currentSession.updatedAt = Date.now();
        // Streaming state is tied to the currently opened session only
        state.isStreaming = status === 'running';
      }
    },

    deleteSession(state, action: PayloadAction<string>) {
      const sessionId = action.payload;
      state.sessions = state.sessions.filter(s => s.id !== sessionId);
      state.unreadSessionIds = state.unreadSessionIds.filter((id) => id !== sessionId);

      if (state.currentSessionId === sessionId) {
        state.currentSessionId = null;
        state.currentSession = null;
      }
    },

    deleteSessions(state, action: PayloadAction<string[]>) {
      const sessionIds = new Set(action.payload);
      state.sessions = state.sessions.filter(s => !sessionIds.has(s.id));
      state.unreadSessionIds = state.unreadSessionIds.filter((id) => !sessionIds.has(id));

      if (state.currentSessionId && sessionIds.has(state.currentSessionId)) {
        state.currentSessionId = null;
        state.currentSession = null;
      }
    },

    addMessage(state, action: PayloadAction<{ sessionId: string; message: CoworkMessage }>) {
      const { sessionId, message } = action.payload;

      if (state.currentSession?.id === sessionId) {
        const existingIndex = state.currentSession.messages.findIndex((item) => item.id === message.id);
        if (existingIndex !== -1) {
          state.currentSession.messages[existingIndex].content = message.content;
          if (message.metadata) {
            state.currentSession.messages[existingIndex].metadata = {
              ...state.currentSession.messages[existingIndex].metadata,
              ...message.metadata,
            };
          }
        } else {
          state.currentSession.messages.push(message);
        }
        state.currentSession.updatedAt = message.timestamp;
      }

      // Update session in list
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].updatedAt = message.timestamp;
      }

      markSessionUnread(state, sessionId);
    },

    updateMessageContent(state, action: PayloadAction<{ sessionId: string; messageId: string; content: string }>) {
      const { sessionId, messageId, content } = action.payload;

      if (state.currentSession?.id === sessionId) {
        const messageIndex = state.currentSession.messages.findIndex(m => m.id === messageId);
        if (messageIndex !== -1) {
          state.currentSession.messages[messageIndex].content = content;
        }
      }

      markSessionUnread(state, sessionId);
    },

    setStreaming(state, action: PayloadAction<boolean>) {
      state.isStreaming = action.payload;
    },

    updateSessionPinned(state, action: PayloadAction<{ sessionId: string; pinned: boolean }>) {
      const { sessionId, pinned } = action.payload;
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].pinned = pinned;
      }
      if (state.currentSession?.id === sessionId) {
        state.currentSession.pinned = pinned;
      }
    },

    updateSessionTitle(state, action: PayloadAction<{ sessionId: string; title: string }>) {
      const { sessionId, title } = action.payload;
      const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        state.sessions[sessionIndex].title = title;
        state.sessions[sessionIndex].updatedAt = Date.now();
      }
      if (state.currentSession?.id === sessionId) {
        state.currentSession.title = title;
        state.currentSession.updatedAt = Date.now();
      }
    },

    enqueuePendingPermission(state, action: PayloadAction<CoworkPermissionRequest>) {
      const alreadyQueued = state.pendingPermissions.some(
        (permission) => permission.requestId === action.payload.requestId
      );
      if (alreadyQueued) return;
      state.pendingPermissions.push(action.payload);
    },

    dequeuePendingPermission(state, action: PayloadAction<{ requestId?: string } | undefined>) {
      const requestId = action.payload?.requestId;
      if (!requestId) {
        state.pendingPermissions.shift();
        return;
      }
      state.pendingPermissions = state.pendingPermissions.filter(
        (permission) => permission.requestId !== requestId
      );
    },

    clearPendingPermissions(state) {
      state.pendingPermissions = [];
    },

    setConfig(state, action: PayloadAction<CoworkConfig>) {
      state.config = action.payload;
    },

    updateConfig(state, action: PayloadAction<Partial<CoworkConfig>>) {
      state.config = { ...state.config, ...action.payload };
    },

    clearCurrentSession(state) {
      state.currentSessionId = null;
      state.currentSession = null;
      state.isStreaming = false;
    },
  },
});

export const {
  setCoworkActive,
  setSessions,
  setCurrentSessionId,
  setCurrentSession,
  setDraftForSession,
  setDraftPrompt,
  setQueuedMessageForSession,
  setQueueWithdrawnForSession,
  addSession,
  updateSessionStatus,
  deleteSession,
  deleteSessions,
  addMessage,
  updateMessageContent,
  setStreaming,
  updateSessionPinned,
  updateSessionTitle,
  enqueuePendingPermission,
  dequeuePendingPermission,
  clearPendingPermissions,
  setConfig,
  updateConfig,
  clearCurrentSession,
} = coworkSlice.actions;

export default coworkSlice.reducer;
